import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "./utils/apiClient";
import ButtonPrimary from "./components/ButtonPrimary";
import { useRegister } from "./RegisterContext.js";
import logo from "./resources/cadmus_dark.svg";

function RegisterScreen() {
  const navigate = useNavigate();
  const { registerData, updateRegisterData, resetRegisterData } = useRegister();

  const [email, setEmail] = useState(registerData.email || "");
  const [password, setPassword] = useState(registerData.password || "");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    const sanitizedEmail = email.trim();
    const sanitizedPassword = password.trim();

    if (!sanitizedEmail || !sanitizedPassword) {
      setError("Email and password are required");
      return;
    }

    if (sanitizedPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      // organization_id = 0 placeholder; will be updated after Lab Info step
      const response = await apiClient.post("/register", {
        email: sanitizedEmail,
        password: sanitizedPassword,
        name: sanitizedEmail, // temporary, will be updated in Lab Info
        organization_id: 0,
      });

      // Register endpoint stuurt nu een short-lived registration token terug
      const registrationToken = response?.data?.token;
      if (!registrationToken) {
        setError("Unexpected response from server. Please try again.");
        return;
      }

      // Reset context én lokale velden zodat volgende Create Account fris begint
      resetRegisterData();
      setEmail("");
      setPassword("");

      // Sla registration token op — wordt gebruikt door /organization
      localStorage.setItem("token", registrationToken);

      updateRegisterData({
        email: sanitizedEmail,
        password: sanitizedPassword,
        token: registrationToken,
      });

      navigate("/register/lab-info");

    } catch (err) {
      console.error("Register error:", err);
      const message =
        err.response?.data?.message ||
        "Registration failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <img
          src={logo}
          alt="Cadmus Logo"
          className="logo"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        />

        <span className="login-header">Create Account</span>
        <span className="login-sub-header">
          Welcome to Cadmus, your all-in-one inbox.
          <br />
          Create your account to get started.
        </span>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleRegister}>
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              required
              autoComplete="off"
              style={{
                color: "#111",
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "16px",
                opacity: email ? 1 : 0.4,
              }}
            />
          </div>

          <div className="form-group" style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              autoComplete="new-password"
              style={{
                color: "#111",
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "16px",
                opacity: password ? 1 : 0.4,
                paddingRight: "40px",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                color: "#999",
              }}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                // Eye open
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              ) : (
                // Eye off
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              )}
            </button>
          </div>

          <ButtonPrimary type="submit" disabled={loading}>
            {loading ? "Creating account..." : "Create Account"}
          </ButtonPrimary>
        </form>

        <p
          style={{
            marginTop: "20px",
            fontSize: "12px",
            color: "#666",
            textAlign: "center",
            lineHeight: "1.6",
          }}
        >
          By signing up you agree to our{" "}
          <a
            href="/terms"
            style={{ color: "#111", fontWeight: "600", textDecoration: "none" }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            style={{ color: "#111", fontWeight: "600", textDecoration: "none" }}
          >
            Privacy Policy
          </a>
          .
        </p>

        <p
          style={{
            marginTop: "12px",
            fontSize: "13px",
            color: "#666",
            textAlign: "center",
          }}
        >
          Already have an account?{" "}
          <span
            onClick={() => navigate("/login")}
            style={{
              color: "#6C44D4",
              fontWeight: "600",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            Sign in
          </span>
        </p>
      </div>
    </div>
  );
}

export default RegisterScreen;